/* The URL, and why the app has one.
 *
 * Where you are lives in the store — `screen`, plus the job code, customer name
 * or settings section that goes with it. The URL is a projection of that: every
 * destination has a path, the path is written as you move, and on boot the path
 * is read back. So a refresh on a job stays on that job, the browser's back
 * button walks back through screens, and a path can be handed to someone.
 *
 * The store stays the one source of truth; this file translates in both
 * directions rather than becoming a second one. It applies a route through the
 * store's own actions, so restoring `/jobs/J-1042` sets up the item rows and
 * tasks exactly as clicking the card does.
 *
 * The server already serves index.html for every extension-less path
 * (`backend/src/static.ts`), and Vite's dev server does the same, so a deep path
 * survives a refresh in both. */
import type { Screen, SettingsSection } from '@/types';
import { unsaved, useApp } from '@/store/useApp';

interface Route {
  screen: Screen;
  jobCode?: string;
  custName?: string;
  settings?: SettingsSection;
}

const SECTIONS: SettingsSection[] = ['stages', 'colours', 'people', 'org'];

const isSection = (v: string): v is SettingsSection => (SECTIONS as string[]).includes(v);

/** The path for where the app currently is. Total over `Screen`, so a new
 *  destination is a compile error here until it has an address. */
export function pathFor(r: Route): string {
  switch (r.screen) {
    case 'dashboard':
      return '/dashboard';
    case 'board':
      return '/board';
    case 'jobs':
      return '/jobs';
    case 'job':
      return `/jobs/${encodeURIComponent(r.jobCode ?? '')}`;
    case 'items':
      return `/jobs/${encodeURIComponent(r.jobCode ?? '')}/items`;
    case 'schedule':
      return '/schedule';
    case 'customers':
      return '/customers';
    case 'customer':
      return `/customers/${encodeURIComponent(r.custName ?? '')}`;
    case 'settings':
      return `/settings/${r.settings ?? 'colours'}`;
  }
}

/** What a path means, or null if it means nothing — `/` included, which is what
 *  an install shortcut and a typed domain both land on. */
export function routeFor(path: string): Route | null {
  const [head, second, third] = path.split('/').filter(Boolean).map(decodeURIComponent);

  switch (head) {
    case 'dashboard':
      return { screen: 'dashboard' };
    case 'board':
      return { screen: 'board' };
    case 'schedule':
      return { screen: 'schedule' };
    case 'jobs':
      if (!second) return { screen: 'jobs' };
      return third === 'items'
        ? { screen: 'items', jobCode: second }
        : { screen: 'job', jobCode: second };
    case 'customers':
      return second ? { screen: 'customer', custName: second } : { screen: 'customers' };
    case 'settings':
      return { screen: 'settings', settings: second && isSection(second) ? second : undefined };
    default:
      return null;
  }
}

/** Put the app where a path says, through the actions a click would use. A job
 *  code or customer that no longer exists leaves the app where it is — the
 *  caller canonicalises the URL afterwards, so a dead link corrects itself
 *  rather than sitting there lying. */
function applyRoute(route: Route | null): void {
  const st = useApp.getState();
  if (!route) return;

  // The back button is a navigation, so it asks the same question the tabs do.
  // The check is here as well as inside the store's actions because the
  // settings branch below sets its section before it moves, and half a move is
  // worse than none. `canonicalise` in the caller puts the address bar back
  // while the shop decides; choosing to leave re-runs this with the forms
  // already restored, so the second pass goes through.
  if (unsaved(st)) return st.leave(() => applyRoute(route));

  switch (route.screen) {
    case 'job':
    case 'items': {
      if (!route.jobCode) return;
      st.openJob(route.jobCode);
      if (route.screen === 'items' && useApp.getState().screen === 'job') {
        st.set('screen', 'items');
      }
      return;
    }
    case 'customer': {
      if (route.custName) st.openCustomer(route.custName);
      return;
    }
    case 'settings': {
      if (route.settings) st.set('settings', route.settings);
      st.go('settings');
      return;
    }
    default:
      st.go(route.screen);
  }
}

/** The path for the state as it stands, written without adding history. */
function canonicalise(): void {
  const path = pathFor(useApp.getState());
  if (path !== window.location.pathname) {
    window.history.replaceState(null, '', path + window.location.search);
  }
}

/** Boot: go where the address bar says, then make the address bar honest. */
export function restoreFromUrl(): void {
  applyRoute(routeFor(window.location.pathname));
  canonicalise();
}

/** Keep the two in step for as long as somebody is signed in. Returns the
 *  teardown. */
export function startUrlSync(): () => void {
  const onPop = () => {
    applyRoute(routeFor(window.location.pathname));
    canonicalise();
  };
  window.addEventListener('popstate', onPop);

  const unsubscribe = useApp.subscribe((s, prev) => {
    if (
      s.screen === prev.screen &&
      s.jobCode === prev.jobCode &&
      s.custName === prev.custName &&
      s.settings === prev.settings
    ) {
      return;
    }
    const path = pathFor(s);
    if (path !== window.location.pathname) {
      window.history.pushState(null, '', path + window.location.search);
    }
  });

  return () => {
    window.removeEventListener('popstate', onPop);
    unsubscribe();
  };
}

/** Signing out takes the address with it: the next person to sign in on a
 *  shared iPad starts at the board, not at whatever job was open. */
export function clearUrl(): void {
  if (window.location.pathname !== '/') {
    window.history.replaceState(null, '', '/' + window.location.search);
  }
}
